import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Grid, TextField, MenuItem, Button, Typography, Table, TableHead, TableRow, TableCell, TableBody, Stack, Chip, Alert } from '@mui/material';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import { printReport, textCol, currencyCol } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';

function fmt(n) {
  return new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

export default function KBPKReport() {
  const [lists, setLists]       = useState([]);
  const [listId, setListId]     = useState('');
  const [rows, setRows]         = useState([]);
  const [workers, setWorkers]   = useState([]);
  const [policy, setPolicy]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    Promise.all([
      apiService().get('salariesmonthlylists'),
      apiService().query('getActiveFiscalPolicy', ''),
      apiService().get('workers'),
    ]).then(([l, p, w]) => {
      if (l?.success) setLists(l.data || []);
      if (p?.success && p.data?.length) setPolicy(p.data[0]);
      if (w?.success) setWorkers(w.data || []);
    });
  }, []);

  useEffect(() => {
    if (!listId) return setRows([]);
    setLoading(true);
    apiService().query('getKBPKReport', `listId=${listId}`)
      .then(r => { if (r?.success) setRows(r.data || []); })
      .finally(() => setLoading(false));
  }, [listId]);

  const totals = useMemo(() => ({
    gross:    rows.reduce((s, r) => s + (r.GrossSalary || 0), 0),
    penEmp:   rows.reduce((s, r) => s + (r.PensionEmployee || 0), 0),
    penEr:    rows.reduce((s, r) => s + (r.PensionEmployer || 0), 0),
    total:    rows.reduce((s, r) => s + (r.TotalPension || 0), 0),
  }), [rows]);

  const selectedList = lists.find(l => Number(l.id || l.Id) === Number(listId));

  const exportCsv = () => {
    const headers = [t('report.nr'), 'NID', t('report.pensionNumber'), t('report.firstName'), t('report.lastName'), t('report.grossSalary'), t('report.pensionWorker5'), t('report.pensionEmployer5'), t('report.pensionTotal')];
    const lines = [headers.join(',')];
    rows.forEach((r, i) => {
      const row = [
        i + 1, r.NID || '', r.PensionNumber || '',
        r.FirstName, r.LastName,
        r.GrossSalary, r.PensionEmployee, r.PensionEmployer, r.TotalPension,
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`);
      lines.push(row.join(','));
    });
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `kbpk_${selectedList?.month || ''}_${selectedList?.year || ''}.csv`;
    a.click();
  };

  const exportPdf = () => {
    const period = selectedList ? `${selectedList.month}/${selectedList.year}` : '';
    const pdfRows = rows.map((r, i) => ({ ...r, _nr: i + 1, _name: r.FullName || `${r.FirstName || ''} ${r.LastName || ''}`.trim() }));
    const penEmpRate = policy?.PensionEmployee || 5;
    const penErRate = policy?.PensionEmployer || 5;
    printReport({
      title: t('reports.kbpk'),
      subtitle: `${t('report.kbpkSubtitle')}${period ? ` | ${t('report.period')}: ${period}` : ''}`,
      orientation: 'landscape',
      columns: [
        { label: t('report.nr'), key: '_nr', align: 'right', width: '4%' },
        textCol('NID', 'NID', '13%'),
        textCol(t('report.pensionNumber'), 'PensionNumber', '12%'),
        textCol(t('report.fullName'), '_name', '20%'),
        currencyCol(t('report.grossSalary'), 'GrossSalary', '13%'),
        currencyCol(`${t('report.pensionWorker')} (${penEmpRate}%)`, 'PensionEmployee', '14%'),
        currencyCol(`${t('report.pensionEmployer')} (${penErRate}%)`, 'PensionEmployer', '14%'),
        currencyCol(t('report.pensionTotal'), 'TotalPension', '10%'),
      ],
      rows: pdfRows,
      totals: { GrossSalary: totals.gross, PensionEmployee: totals.penEmp, PensionEmployer: totals.penEr, TotalPension: totals.total },
      period,
      badge: { label: `${rows.length} ${t('report.employed')}` },
      extraInfo: [t('report.kbpkConfidential'), t('report.kbpkDeadline')],
    });
  };

  return (
    <ReportPage>
      <PageHeader
        title={t('reports.kbpk')}
        actions={
          <Button component={Link} to="/home/reports" variant="outlined">{t('report.backLabel')}</Button>
        }
      />

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>{t('report.kbpkDeadlineLabel')}:</strong> {t('report.kbpkDeadlineText')}
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth label={t('report.monthlyPayrollList')}
                value={listId} onChange={e => setListId(e.target.value)}
              >
                <MenuItem value="">{t('report.selectMonthPlaceholder')}</MenuItem>
                {lists.map(l => (
                  <MenuItem key={l.id || l.Id} value={l.id || l.Id}>
                    {l.name || `${l.month}/${l.year}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {policy && (
              <Grid item xs={12} sm={6} md={4}>
                <Stack spacing={0.5}>
                  <Chip label={`${t('report.pensionWorker')}: ${policy.PensionEmployee}%`} color="info" size="small" />
                  <Chip label={`${t('report.pensionEmployer')}: ${policy.PensionEmployer}%`} color="info" size="small" />
                  <Typography variant="caption" color="text.secondary">{policy.Name}</Typography>
                </Stack>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <Stack direction="row" spacing={1}>
                <ExportMenu onCsv={exportCsv} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} disabled={!rows.length} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <>
          <Card sx={{ mb: 1 }}>
            <CardContent sx={{ py: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Typography variant="body2"><strong>{t('report.grandTotal')} Bruto:</strong> {fmt(totals.gross)}</Typography>
                <Typography variant="body2"><strong>{t('report.pensionWorker')} (5%):</strong> {fmt(totals.penEmp)}</Typography>
                <Typography variant="body2"><strong>{t('report.pensionEmployer')} (5%):</strong> {fmt(totals.penEr)}</Typography>
                <Typography variant="body2" color="primary.main"><strong>{t('report.pensionFundTotal')}:</strong> {fmt(totals.total)}</Typography>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('report.nr')}</TableCell>
                    <TableCell>NID</TableCell>
                    <TableCell>{t('report.pensionNumber')}</TableCell>
                    <TableCell>{t('report.fullName')}</TableCell>
                    <TableCell align="right">{t('report.grossSalary')}</TableCell>
                    <TableCell align="right">{t('report.pensionWorker5')}</TableCell>
                    <TableCell align="right">{t('report.pensionEmployer5')}</TableCell>
                    <TableCell align="right">{t('report.pensionTotal')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={r.WorkerId} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">{r.NID || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">{r.PensionNumber || '—'}</Typography>
                      </TableCell>
                      <TableCell>{r.FullName}</TableCell>
                      <TableCell align="right">{fmt(r.GrossSalary)}</TableCell>
                      <TableCell align="right">{fmt(r.PensionEmployee)}</TableCell>
                      <TableCell align="right">{fmt(r.PensionEmployer)}</TableCell>
                      <TableCell align="right"><strong>{fmt(r.TotalPension)}</strong></TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'action.selected' }}>
                    <TableCell colSpan={4}><strong>{t('report.total').toUpperCase()}</strong></TableCell>
                    <TableCell align="right"><strong>{fmt(totals.gross)}</strong></TableCell>
                    <TableCell align="right"><strong>{fmt(totals.penEmp)}</strong></TableCell>
                    <TableCell align="right"><strong>{fmt(totals.penEr)}</strong></TableCell>
                    <TableCell align="right"><strong>{fmt(totals.total)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {listId && !loading && rows.length === 0 && (
        <Alert severity="info">{t('report.noDataForSelectedMonth')}</Alert>
      )}

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.kbpk')}
        subtitle={selectedList ? `${selectedList.month}/${selectedList.year}` : ''}
        columns={[
          { label: t('report.fullName'), key: 'FullName', align: 'left' },
          { label: 'NID', key: 'NID', align: 'left' },
          { label: t('report.grossSalary'), key: 'GrossSalary', align: 'right', format: 'currency' },
          { label: t('report.pensionWorker'), key: 'PensionEmployee', align: 'right', format: 'currency' },
          { label: t('report.pensionEmployer'), key: 'PensionEmployer', align: 'right', format: 'currency' },
          { label: t('report.pensionTotal'), key: 'TotalPension', align: 'right', format: 'currency' },
        ]}
        rows={rows}
        totals={{ GrossSalary: totals.gross, PensionEmployee: totals.penEmp, PensionEmployer: totals.penEr, TotalPension: totals.total }}
        workers={workers}
      />
    </ReportPage>
  );
}
